import React from "react";
import { useTranslation } from "react-i18next";
import { LiveEventDetails } from "@/hooks/useLiveEventDetails";
import { Skeleton } from "@/components/ui";
import {
  LiveEventTypeEnum,
  LiveEventAudienceEnum,
} from "@/__generated__/types";

interface DetailsProps {
  liveEvent: LiveEventDetails | null;
  loading: boolean;
  error: unknown;
}

interface DetailItem {
  label: string;
  value: string;
  isEventType?: boolean;
}

interface DetailSection {
  type: "single" | "grid";
  columns?: number;
  hasDividerBelow?: boolean;
  hasDividerAbove?: boolean;
  items: DetailItem[];
}

const DetailItem: React.FC<DetailItem> = ({
  label,
  value,
  isEventType = false,
}) => (
  <div>
    <h3
      className={`${isEventType ? "mt-2" : "mt-8"} mb-2 text-sm leading-5 font-bold text-gray-700`}
    >
      {label}
    </h3>
    <p className="mb-5 text-sm leading-5 font-medium text-gray-500/60">
      {value}
    </p>
  </div>
);

const DetailSection: React.FC<{ section: DetailSection }> = ({ section }) => {
  const gridClass =
    section.type === "grid"
      ? `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${section.columns || 4} gap-8`
      : "";

  const containerClass = [
    section.hasDividerBelow && "border-b border-border",
    section.hasDividerAbove && "border-t border-border",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass}>
      <div className={gridClass}>
        {section.items.map((item) => (
          <DetailItem key={item.label} {...item} />
        ))}
      </div>
    </div>
  );
};

export const Details: React.FC<DetailsProps> = ({
  liveEvent,
  loading,
  error,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-border bg-card p-6">
          {/* Event Type skeleton */}
          <div className="border-b border-border">
            <Skeleton className="mt-2 mb-2 h-5 w-24" />
            <Skeleton className="mb-5 h-5 w-32" />
          </div>

          {/* Main details grid skeleton */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Skeleton className="mt-8 mb-2 h-5 w-20" />
              <Skeleton className="mb-5 h-5 w-16" />
            </div>
            <div>
              <Skeleton className="mt-8 mb-2 h-5 w-16" />
              <Skeleton className="mb-5 h-5 w-24" />
            </div>
            <div>
              <Skeleton className="mt-8 mb-2 h-5 w-24" />
              <Skeleton className="mb-5 h-5 w-20" />
            </div>
            <div>
              <Skeleton className="mt-8 mb-2 h-5 w-32" />
              <Skeleton className="mb-5 h-5 w-28" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!liveEvent) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">
            {t("eventDetails.tabs.details")}
          </h2>
          <p className="text-muted-foreground">
            {error
              ? t("global.error", { message: "Failed to load event details" })
              : t("global.loading")}
          </p>
        </div>
      </div>
    );
  }

  const formatEventType = (eventType: LiveEventTypeEnum | null) => {
    if (!eventType) return "-";

    switch (eventType) {
      case LiveEventTypeEnum.LiveEvent:
        return t("eventDetails.details.eventType.liveEvent");
      case LiveEventTypeEnum.BotEvent:
        return t("eventDetails.details.eventType.automatedGame");
      default:
        return eventType;
    }
  };

  const formatAudience = (audience: LiveEventAudienceEnum | null) => {
    if (!audience) return "-";

    switch (audience) {
      case LiveEventAudienceEnum.Everyone:
        return t("eventDetails.details.audienceType.everyone");
      case LiveEventAudienceEnum.Admits:
        return t("eventDetails.details.audienceType.admits");
      case LiveEventAudienceEnum.Applicants:
        return t("eventDetails.details.audienceType.applicants");
      case LiveEventAudienceEnum.Commits:
        return t("eventDetails.details.audienceType.commits");
      case LiveEventAudienceEnum.Other:
        return t("eventDetails.details.audienceType.other");
      default:
        return audience;
    }
  };

  const formatDateTime = (timestamp: number) => {
    try {
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "-";
    }
  };

  // Build sections data structure
  const sections: DetailSection[] = [
    // Event Type section
    {
      type: "single",
      hasDividerBelow: true,
      items: [
        {
          label: t("eventDetails.details.titles.eventType"),
          value: formatEventType(liveEvent.eventType),
          isEventType: true,
        },
      ],
    },
    // Main details grid
    {
      type: "grid",
      columns: 4,
      items: [
        {
          label: t("eventDetails.details.titles.eventName"),
          value: liveEvent.name || "-",
        },
        {
          label: t("eventDetails.details.titles.audience"),
          value: formatAudience(liveEvent.audience),
        },
        {
          label: t("eventDetails.details.titles.chatChannel"),
          value: liveEvent.chatChannel?.displayName || "-",
        },
        {
          label: t("eventDetails.details.titles.startDate"),
          value: liveEvent.startsAt ? formatDateTime(liveEvent.startsAt) : "-",
        },
      ],
    },
  ];

  // Add game details section if it's a bot event with game details
  if (
    liveEvent.eventType === LiveEventTypeEnum.BotEvent &&
    (liveEvent.gameType || liveEvent.gameVariation)
  ) {
    sections.push({
      type: "grid",
      columns: 4, // Keep it 4 columns to match the main grid
      hasDividerAbove: true,
      items: [
        {
          label: t("eventDetails.details.titles.gameType"),
          value: liveEvent.gameType || "-",
        },
        {
          label: t("eventDetails.details.titles.gameVariation"),
          value: liveEvent.gameVariation || "-",
        },
      ],
    });
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-6">
        {sections.map((section, index) => (
          <DetailSection key={index} section={section} />
        ))}
      </div>
    </div>
  );
};
